





















































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { LazyLoader } from "../../../../../base/components/system";
import { ModelEditableMixin } from "../../../../../base/components/system/mixins";
import { IBehaviorDocumentEditorObjectLazyDrawerConfiguration } from "../../../../../base/domain/configuration";
import { Point, Size } from "../../../../../base/domain/drawing";
import { DomainRegistry } from "../../../../domain";
import { IConfiguration } from "../../../../domain/configuration";
import { IFormatObjectEditable, IFormatObjectTemporaryImage, FormatPageStructureEditor } from "../../../../domain/editableFormat";
import { FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, UploadImageFileMixin, QuestionnaireGuidAssignableMixin } from "../mixins";
import { QuestionnaireFormatPageInputTracker, QuestionnaireFormatPageLocator, IFormatPageLocatable, QuestionnaireFormatPageRelocator } from "./trackers";
import { default as QuestionnaireFormatPageObjectDrawer } from "./QuestionnaireFormatPageObjectDrawer.vue";
import { default as FormatPageObjectMenu } from "./FormatPageObjectMenu.vue";
import { instanceOfISizePropertyEditable } from "../../../../domain/editableFormat/properties";
import { QuestionnaireGuidTreeEditor, QuestionnaireGuidTree } from "../../../../domain/questionnaire";
import { QuestionnaireFormatObject } from "../../../../domain/questionnaire/constants";

@Component({
  components: {
    QuestionnaireFormatPageInputTracker,
    QuestionnaireFormatPageObjectDrawer,
    FormatPageObjectMenu,
    QuestionnaireFormatPageLocator,
    QuestionnaireFormatPageRelocator,
    LazyLoader,
  }
})
export default class QuestionnaireFormatPageEditor extends Mixins(ModelEditableMixin, FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, UploadImageFileMixin, QuestionnaireGuidAssignableMixin) {
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop({ default: 1 })
  scale: number;

  @Prop({ default: () => new Size(10, 10) })
  gridSize: Size;

  @Prop()
  isActive: boolean;

  $refs!: {
    page: HTMLDivElement,
    menu: FormatPageObjectMenu,
    tracker: QuestionnaireFormatPageInputTracker,
    locator: QuestionnaireFormatPageLocator,
    relocator: QuestionnaireFormatPageRelocator,
    lazyLoader: LazyLoader,
  };
  $configuration!: IConfiguration;

  data() {
    return {
      pageElement: null as HTMLDivElement,
      drawableObjects: [] as IFormatObjectEditable[],
      isRelocating: false,
    };
  }
  pageElement: HTMLDivElement;
  drawableObjects: IFormatObjectEditable[];
  isRelocating: boolean;

  mounted() {
    this.pageElement = this.$refs.page;
  }

  updated() {
    this.$refs.lazyLoader.loadNext();
  }

  get treeItems(): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    return QuestionnaireGuidTree.from(this.drawableObjects);
  }

  get objectContainerStyle() {
    return {
      padding: `0 ${QuestionnaireFormatObject.Gap}px`,
    };
  }

  get locatorStyle() {
    return {
      height: `${QuestionnaireFormatObject.Gap}px`,
    };
  }

  get lazyDrawerConfiguration(): IBehaviorDocumentEditorObjectLazyDrawerConfiguration {
    return this.$configuration.formatEditor.behavior.format.editor.objectLazyDrawer;
  }

  @Watch("editingSource")
  onEditingSourceUpdated(newValue: FormatPageStructureEditor, oldValue: FormatPageStructureEditor) {
    if (!newValue.isReferedSameSource(oldValue)) {
      this.$refs.lazyLoader.restart();
    }
  }

  getLocator(): IFormatPageLocatable {
    return this.$refs.locator;
  }

  get pageSizeStyleProperties() {
    const image = this.image;
    if (!image) {
      return null;
    }
    const isHorizontal = this.isHorizontal;
    return {
      width: (isHorizontal ? image.size.height : image.size.width) + this.toLevelWidth() + "px",
      height: Math.max((isHorizontal ? image.size.width : image.size.height), (this.totalHeightOfObjectsFits + (QuestionnaireFormatObject.Gap * 3))) + "px"
    };
  }

  private toLevelWidth(): number {
    return Math.max(0, ...this.treeItems.map(v => v.level)) * QuestionnaireFormatObject.Gap;
  }

  get totalHeightOfObjectsFits(): number {
    // チェックボックスは取得したサイズより2px大きく表示されているため、ページの高さやオブジェクト追加可能位置の描画がずれてしまう
    return this.objectHeights.reduce((p, c) => p += c + QuestionnaireFormatObject.Gap, 0) + (QuestionnaireFormatObject.Gap * 2);
  }

  get objectHeights(): number[] {
    return this.drawableObjects.map(v => instanceOfISizePropertyEditable(v) ? v.size().getValue().height : 0);
  }

  toObjectIndex(location: Point): number {
    let index = 0;
    let y = QuestionnaireFormatObject.Gap / 2;
    for (; index < this.objectHeights.length; index++) {
      const offset = this.objectHeights[index] + QuestionnaireFormatObject.Gap;
      if (location.y < (y + offset)) {
        break;
      }
      y += offset;
    }
    return index;
  }

  get layerRectangleStyleProperties() {
    const layer = this.layer;
    if (!layer || !layer.imageGuid) {
      return null;
    }
    return {
      left: layer.location.x + "px",
      top: layer.location.y + "px",
      width: layer.size.width + "px",
      height: layer.size.height + "px"
    };
  }

  get layerSource(): string {
    const layer = this.layer;
    if (!layer || !layer.imageGuid) {
      return null;
    }
    const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
    const imageSource = layer.formatId
      ? repository.getLayerImageSource(layer.formatId, layer.imageGuid)
      : repository.getTemporaryImageSource(layer.imageGuid);
    return imageSource.source;
  }

  get temporaryImages(): IFormatObjectTemporaryImage[] {
    return this.storePresenter.getTemporaryImages();
  }

  onDragHandleSelected(source: IFormatObjectEditable, event: PointerEvent): void {
    event.stopPropagation();
    if (this.$refs.menu.shouldShow) {
      return;
    }
    this.isRelocating = true;
    const baseSource = source.isSelected()
      ? this.drawableObjects.find(v => v.isSelected())
      : source;
    this.$refs.relocator.layoutRectangleObject(this, baseSource)
      .then(r => {
        if (!r || typeof r.index !== "number" || r.index === -1) {
          return;
        }
        this.writePropertyWhenValid(s => {
          new QuestionnaireGuidTreeEditor(s).insertSelectedObjects(r.index, r.isHitObject, r.isNextToObject);
        });
      })
      .finally(() => this.isRelocating = false);
  }

  onObjectMouseDownCaptured(sender: IFormatObjectEditable, event: PointerEvent): void {
    this.$refs.menu.handleMouseDownCaptured(sender, event);
  }

  onObjectMouseRightDownHandled(event: PointerEvent): void {
    // オブジェクト上で右クリックメニューを表示する場合、イベントの通知を止めているため、ここでトラッカーのフォーカスをリセットすることによりキーボード操作が可能になる
    this.$refs.tracker.resetFocus();
  }

  onPageMouseRightDownHandled(event: PointerEvent): void {
    this.$refs.menu.handlePageMouseRightDowned(event);
  }

  onMenuClosed(): void {
    this.$refs.tracker.resetFocus();
  }
}
